import {
    subDays, addDays, eachDayOfInterval, format, isWithinInterval, parse, startOfDay,
} from 'date-fns';
import _ from 'underscore';

import { ProductForHotelFilters } from '@ve/components/hotel/filters/common/store/types';
import { filterToProductType } from '@ve/services/productTypeConverter';
import {
    IDatesTabsStore,
    ShortProductForHotelFilters,
} from './types/types';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
    namespaced: true,
    state: () => ({
        manuallySelectedDate: null,
        searchDates: [],
    }),
    mutations: {
        clearManuallyDate(state) {
            state.manuallySelectedDate = null;
        },
        setSearchDates(state, dates) {
            state.searchDates = dates;
        },
        addSearchDate(state, date) {
            if (state.searchDates.indexOf(date) === -1) {
                state.searchDates.push(date);
            }
        },
        selectDateManually(state, date) {
            state.manuallySelectedDate = date;
        },
    },
    actions: {
        selectDateManually({ commit, getters }, date): void {
            commit('selectDateManually', date || getters.selectedDate);
        },
        startedNewSearch({ commit, getters }): void {
            commit('clearManuallyDate');
            commit('setSearchDates', [...getters.datesForSearchInterval] || []);
        },
    },
    getters: {
        loadingState: (_state, _getters, { mapForFilters: { loadingState } }) => loadingState,
        productsAvailable:
            (_state, _getters, { mapForFilters: { products = [] } }) => !!(products && products.length),
        productsDataByProductType: (_state, _getters, { mapForFilters: { productType, filteredTourProducts = [] } }) => (
            productType === 'all' ? filteredTourProducts : filteredTourProducts.filter((product) => product.type === filterToProductType(productType))
        ),
        tourProductDatesAndPrices: (_state, _getters, { mapForFilters: { filteredTourProducts = [] } }) => filteredTourProducts.reduce(
            (datesAndPrices, product) => {
                if (product.type === 'TourProduct' && (!datesAndPrices[product.startDate] || datesAndPrices[product.startDate].price > product.price)) {
                    datesAndPrices[product.startDate] = product;
                }
                return datesAndPrices;
            }, {} as { [key: string]: ProductForHotelFilters },
        ),
        expandedDates: (_state, _getters, { mapForFilters: { checkInDateRange } }) => {
            const dateFlexible = 14;
            const {
                from,
                to,
            } = checkInDateRange;

            const middleDate = from && to ? new Date((from.getTime() + to.getTime()) / 2) : null;
            const flexibleDateFrom = middleDate ? subDays(middleDate, dateFlexible) : null;
            const flexibleDateTo = middleDate ? addDays(middleDate, dateFlexible) : null;
            const tomorrow = addDays(startOfDay(new Date()), 1);
            const startDateForTabs = flexibleDateFrom && from < flexibleDateFrom ? from : flexibleDateFrom;
            const endDateForTabs = flexibleDateTo && to > flexibleDateTo ? to : flexibleDateTo;

            return {
                startDateForTabs: startDateForTabs && startDateForTabs < tomorrow ? tomorrow : startDateForTabs,
                endDateForTabs,
            };
        },
        datesForSearchInterval: (_state, _getters, { mapForFilters: { checkInDateRange } }) => {
            let {
                from,
                to,
            } = checkInDateRange;
            if (!(from && to)) return [];
            if (from.getTime() === to.getTime()) {
                from = subDays(from, 1);
                to = addDays(to, 1);
            }

            return eachDayOfInterval({
                start: from,
                end: to,
            }).map((date) => format(date, DATE_FORMAT));
        },
        datesAndPrices: (_state,
                         { tourProductDatesAndPrices, productsDataByProductType, expandedDates },
                         { mapForFilters: { productType } }) => {
            const {
                startDateForTabs,
                endDateForTabs,
            } = expandedDates;
            if (!(startDateForTabs && endDateForTabs)) return null;

            const productsDatesAndPrices = productsDataByProductType.reduce((datesAndPrices, product) => {
                const startDateItem = datesAndPrices[product.startDate];
                if (!startDateItem || (startDateItem.price && startDateItem.price > product.price)) {
                    datesAndPrices[product.startDate] = product;
                }
                return datesAndPrices;
            }, {} as { [key: string]: ShortProductForHotelFilters });

            const emptyDates = eachDayOfInterval({
                start: startDateForTabs,
                end: endDateForTabs,
            }).reduce((dates, date) => {
                const dateStr = format(date, DATE_FORMAT);
                dates[dateStr] = {
                    price: null,
                    type: null,
                    startDate: dateStr,
                };
                return dates;
            }, {} as { [key: string]: ShortProductForHotelFilters });

            return {
                ...emptyDates,
                ...productsDatesAndPrices,
                ...productType === 'all' ? tourProductDatesAndPrices : {},
            };
        },
        bestPriceDate: (_state, { datesAndPrices }, { mapForFilters: { productType } }) => {
            if (productType === 'all') {
                return null;
            }
            const bestPriceDateObject = _.min(Object.values(datesAndPrices), (dateItem) => (dateItem.price ? dateItem.price : Infinity));
            if (bestPriceDateObject === Infinity) return null;
            return typeof bestPriceDateObject === 'object' && bestPriceDateObject.price ? bestPriceDateObject.startDate : null;
        },
        selectedDate: (
            { manuallySelectedDate },
            { datesAndPrices, productsAvailable, tourProductDatesAndPrices },
            { mapForFilters: { checkInDateRange, productType } },
        ) => {
            if (manuallySelectedDate && datesAndPrices[manuallySelectedDate]) return manuallySelectedDate;
            const {
                from,
                to,
            } = checkInDateRange;
            if (!(from && to) || !productsAvailable) return null;
            if (from.getTime() === to.getTime()) return format(from, DATE_FORMAT);

            //choose best date only between tour product if 'all' filter selected
            const datesSource = productType === 'all' && !_.isEmpty(tourProductDatesAndPrices) ? tourProductDatesAndPrices : datesAndPrices;
            const minPriceDate = _.min(Object.values(datesSource).filter((dateItem) => isWithinInterval(parse(dateItem.startDate, DATE_FORMAT, new Date()), {
                start: from,
                end: to,
            })), (dateItem) => (dateItem.price ? dateItem.price : Infinity));
            return typeof minPriceDate === 'object' ? minPriceDate.startDate : null;
        },
    },
} as IDatesTabsStore;